import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wand2, Image as ImageIcon, Download } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import ProductGallery from './ProductGallery';
import geminiAIService, { GenerateContentRequest } from '../services/geminiAIService';
import modelStorageService from '../services/modelStorageService';
import { useAuth } from '../contexts/AuthContext';

interface GenerateWithAIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Product {
  id: string;
  name: string;
  image: string;
  category: string;
  variants?: string[];
}

interface GeneratedContent {
  id: string;
  type: 'image';
  content: string;
  prompt: string;
  products: Product[];
  createdAt: Date;
}

const GenerateWithAIModal: React.FC<GenerateWithAIModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent[]>([]);
  const [showGallery, setShowGallery] = useState(false);

  const handleProductSelect = (product: Product) => {
    console.log('🎯 handleProductSelect llamado con:', product);
    console.log('🎯 Producto imagen:', product.image);
    console.log('🎯 Productos actuales:', selectedProducts);
    console.log('🎯 Límite máximo:', 3);
    
    if (selectedProducts.length < 3 && !selectedProducts.find(p => p.id === product.id)) {
      console.log('✅ Agregando producto a la lista');
      setSelectedProducts(prev => {
        const newList = [...prev, product];
        console.log('✅ Nueva lista de productos:', newList);
        console.log('✅ Primer producto imagen:', newList[0]?.image);
        return newList;
      });
    } else {
      console.log('❌ No se puede agregar producto:', {
        length: selectedProducts.length,
        alreadyExists: selectedProducts.find(p => p.id === product.id)
      });
    }
  };

  const handleProductRemove = (productId: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId));
  };


  const handleGenerate = async () => {
    if (selectedProducts.length === 0) {
        alert('Debes seleccionar al menos un producto');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);

    try {
      // Simular progreso de generación
      const progressInterval = setInterval(() => {
        setGenerationProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // Llamar al servicio real de Gemini
      const request: GenerateContentRequest = {
        contentType: 'image',
        products: selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          category: p.category,
          image: p.image // Agregar la imagen
        })),
        prompt: prompt
      };

      const response = await geminiAIService.generateContent(request);
      
      // Completar progreso
      setGenerationProgress(100);
      clearInterval(progressInterval);
      
      // Guardar modelo en Firebase Storage si el usuario está autenticado
      let savedModelUrl = response.content;
      console.log('💾 Intentando guardar modelo en Storage...');
      console.log('💾 Usuario:', user ? { uid: user.uid, email: user.email } : 'No autenticado');
      console.log('💾 Response success:', response.success);
      
      if (user && response.success) {
        try {
          console.log('💾 Llamando a modelStorageService.saveGeneratedModel...');
          const saveResult = await modelStorageService.saveGeneratedModel(user.uid, {
            imageUrl: response.content,
            prompt: response.prompt,
            products: response.products,
            generatedAt: response.generatedAt
          });
          
          console.log('💾 Resultado del guardado:', saveResult);
          
          if (saveResult.success && saveResult.downloadUrl) {
            savedModelUrl = saveResult.downloadUrl;
            console.log('✅ Modelo guardada en Firebase Storage:', saveResult.downloadUrl);
          } else {
            console.warn('⚠️ Guardado falló:', saveResult.error);
          }
        } catch (error) {
          console.error('❌ Error guardando modelo en Storage:', error);
          // Continuar con la URL original si falla el guardado
        }
      } else {
        console.log('💾 No se guarda en Storage - Usuario:', user ? 'Autenticado' : 'No autenticado', 'Response success:', response.success);
      }
      
      // Agregar contenido generado a la lista
      const newContent: GeneratedContent = {
        id: Date.now().toString(),
        type: response.type,
        content: response.content, // Usar la imagen generada directamente (data URL)
        prompt: response.prompt,
        products: response.products.map(p => ({
          ...p,
          image: p.image || '/placeholder-product.jpg', // Asegurar que image no sea undefined
          variants: p.variants || []
        })),
        createdAt: response.generatedAt
      };
      
      setGeneratedContent(prev => [newContent, ...prev]);
      
    } catch (error) {
      console.error('Error generando contenido:', error);
        alert('Error generando contenido. Inténtalo de nuevo.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  };

  const getContentTypeIcon = (type: string) => {
    return <ImageIcon className="w-5 h-5" />;
  };

  const getContentTypeLabel = (type: string) => {
    return 'Modelo';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <Wand2 className="w-8 h-8 text-primary-500" />
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                  Generar modelo con IA
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
                {/* Selección de productos */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Seleccionar productos
                    </h3>
                    <button
                      onClick={() => setShowGallery(!showGallery)}
                      className="btn-secondary text-sm"
                    >
                      {showGallery ? 'Ocultar galería' : 'Ver galería'}
                    </button>
                  </div>


                  {/* Galería de productos */}
                  {showGallery && (
                    <div className="border border-gray-200 dark:border-slate-700 rounded-xl p-4">
                      <ProductGallery
                        onProductSelect={handleProductSelect}
                        selectedProducts={selectedProducts}
                        maxSelection={3}
                        showActions={false}
                      />
                    </div>
                  )}
                </div>

                {/* Prompt personalizado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Instrucciones específicas para el modelo (opcional)
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="input min-h-[100px] resize-none"
                    placeholder="Describe el estilo del modelo, pose, ambiente, colores de fondo, o cualquier detalle específico que quieras para la modelo vistiendo tus prendas..."
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Las instrucciones ayudarán a la IA a generar un modelo más preciso según tus necesidades.
                  </p>
                </div>

                {/* Progreso de generación */}
                {isGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                      <span>Generando modelo con IA...</span>
                      <span>{generationProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${generationProgress}%` }}
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                      />
                    </div>
                  </div>
                )}

                {/* Contenido generado */}
                {generatedContent.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Modelos generados
                    </h3>
                    <div className="space-y-4">
                      {generatedContent.map((content) => (
                        <div key={content.id} className="card p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              {getContentTypeIcon(content.type)}
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {getContentTypeLabel(content.type)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {content.createdAt.toLocaleTimeString()}
                              </span>
                            </div>
                            <button className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                              <Download size={16} />
                            </button>
                          </div>
                          <div className="mb-3">
                            <img 
                              src={content.content} 
                              alt="Modelo generada" 
                              className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder-model.jpg';
                              }}
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {content.products.map((product) => (
                              <span
                                key={product.id}
                                className="px-2 py-1 bg-gray-100 dark:bg-slate-700 rounded-lg text-xs text-gray-600 dark:text-gray-400"
                              >
                                {product.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-slate-700">
              <button
                onClick={onClose}
                className="btn-secondary"
                disabled={isGenerating}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleGenerate}
                disabled={isGenerating || selectedProducts.length === 0}
                className="btn-primary flex items-center space-x-2"
              >
                <Wand2 size={16} />
                <span>{isGenerating ? 'Generando modelo...' : 'Generar modelo'}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GenerateWithAIModal;
